/**
 * FormerDriversTab — 08/02/2026 Q2 Future/Backlog.
 *
 * Affiche les chauffeurs archivés (`firedDriversArchive`) avec leurs
 * formations / qualifications conservées. Permet le RE-RECRUTEMENT en
 * un clic (réutilise hireDriver qui consomme déjà l'archive).
 *
 * Source unique : `state.firedDriversArchive` keyed by `dataId`.
 */
import React from 'react';
import { ArchiveX, UserPlus, GraduationCap, IdCard, Star, AlertTriangle } from 'lucide-react';
import { DRIVERS } from '../../data/drivers';
import { getDriverPhoto } from '../../utils/driverPhoto';
import { formatMoney } from '../../utils/format';

export default function FormerDriversTab({ archive, hiredIds, treasury, onRehire, t }) {
  const entries = Object.entries(archive || {})
    .filter(([dataId]) => !hiredIds.includes(dataId)) // hide already-rehired
    .sort((a, b) => (b[1].firedAt || 0) - (a[1].firedAt || 0));

  if (entries.length === 0) {
    return (
      <div className="surface-card p-6 text-center" data-testid="former-drivers-empty">
        <ArchiveX className="w-10 h-10 mx-auto text-slate-500 mb-2" />
        <p className="text-sm text-slate-300 font-semibold">Aucun ancien chauffeur archivé</p>
        <p className="text-[11px] text-slate-500 mt-1 max-w-md mx-auto">
          Quand un chauffeur quitte votre entreprise (refus de négo salariale,
          démission, licenciement), ses formations et qualifications sont
          conservées ici. Vous pourrez le ré-engager en un clic — il
          reviendra avec ses compétences acquises.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="former-drivers-tab">
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-md px-3 py-2 flex items-start gap-2">
        <ArchiveX className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-slate-300 leading-snug">
          <strong>{entries.length} ancien{entries.length > 1 ? 's' : ''} chauffeur{entries.length > 1 ? 's' : ''}</strong>{' '}
          dans vos archives. Leurs formations sont conservées. Cliquez sur «&nbsp;Ré-engager&nbsp;»
          pour les rappeler — ils reviendront avec leurs qualifications acquises et
          un bonus de satisfaction (retour aux sources).
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {entries.map(([dataId, archived]) => (
          <FormerDriverCard
            key={dataId}
            dataId={dataId}
            archived={archived}
            canRehire={treasury > 0}
            onRehire={() => onRehire(dataId)}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

function FormerDriverCard({ dataId, archived, canRehire, onRehire }) {
  const catalog = DRIVERS.find(d => d.id === dataId);
  const name = catalog?.name || dataId;
  const photo = getDriverPhoto(catalog || { dataId });
  const initials = name.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const trainings = archived.trainings || [];
  const licenses = normalizeLicensesArr(archived.licenses || catalog?.licenses);
  return (
    <div className="surface-card p-3 flex flex-col gap-2" data-testid={`former-driver-${dataId}`}>
      <div className="flex items-center gap-3">
        {photo ? (
          <img src={photo} alt={name} className="w-12 h-12 rounded-full object-cover border border-slate-500/60 opacity-80" loading="lazy" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center text-sm font-bold text-white">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{name}</p>
          <p className="text-[10px] text-slate-400">
            Niveau {archived.level || 1} • {archived.specialty || '—'}
          </p>
        </div>
      </div>
      <div className="space-y-1 text-[11px]">
        {licenses.length > 0 && (
          <div className="flex items-center gap-1.5">
            <IdCard className="w-3 h-3 text-sky-400" />
            <span className="text-slate-400">Permis :</span>
            <div className="flex flex-wrap gap-0.5">
              {licenses.map((lic) => (
                <span key={lic} className="px-1 py-px bg-sky-500/20 text-sky-200 rounded text-[10px] font-mono">
                  {lic}
                </span>
              ))}
            </div>
          </div>
        )}
        {trainings.length > 0 && (
          <div className="flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3 text-emerald-400" />
            <span className="text-slate-400">Formations :</span>
            <div className="flex flex-wrap gap-0.5">
              {trainings.map((tr) => (
                <span key={tr} className="px-1 py-px bg-emerald-500/20 text-emerald-200 rounded text-[10px] font-mono uppercase">
                  {tr}
                </span>
              ))}
            </div>
          </div>
        )}
        {archived.totalTrips > 0 && (
          <div className="flex items-center gap-1.5 text-slate-400">
            <Star className="w-3 h-3" />
            <span>{archived.totalTrips} courses • {(archived.totalKm || 0).toLocaleString('fr-FR')} km</span>
          </div>
        )}
        {archived.employerCost && (
          <div className="mt-1 pt-1 border-t border-slate-700/40 space-y-0.5">
            <p className="text-[10px] text-slate-500">
              Coût mensuel précédent : {formatMoney(archived.employerCost)}
            </p>
            {/* 08/02/2026 — Prime de réembauche +5 % (REHIRE_PREMIUM). */}
            <p className="text-[10px] text-amber-200/90" data-testid={`former-driver-rehire-cost-${dataId}`}>
              Nouveau coût mensuel (+5 %) : <strong>{formatMoney(Math.round(archived.employerCost * 1.05))}</strong>
            </p>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRehire}
        disabled={!canRehire}
        className={`mt-1 text-xs font-semibold py-1.5 rounded transition-colors flex items-center justify-center gap-1 ${
          canRehire
            ? 'bg-emerald-600/30 border border-emerald-500/50 text-emerald-100 hover:bg-emerald-600/40'
            : 'bg-slate-700/30 border border-slate-600/50 text-slate-400 cursor-not-allowed'
        }`}
        data-testid={`former-driver-rehire-${dataId}`}
      >
        <UserPlus className="w-3.5 h-3.5" />
        Ré-engager
      </button>
      {!canRehire && (
        <p className="text-[10px] text-amber-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Trésorerie insuffisante
        </p>
      )}
    </div>
  );
}

function normalizeLicensesArr(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  return String(raw).split(/[+,;\s\/]/).map(s => s.trim()).filter(Boolean);
}
